import type {
  CellRendererSelectorFunc,
  ICellRendererParams,
} from 'ag-grid-enterprise';

export const CELL_TEST_ID_ATTRIBUTE = 'data-testid';

export type CreateCellTestIdRendererSelectorArgs = {
  /**
   * colId -> data-testid. A column that is not in the map stays untagged.
   *
   * Write the ids as literal strings so they stay greppable — they are a
   *  contract with product analytics and with e2e.
   */
  testIdByColId: Readonly<Record<string, string>>;
  /**
   * Pinned total/summary rows are not data rows, so they are skipped by
   *  default. Tagging them makes per-cell click counts stop matching the
   *  row count.
   */
  includePinnedRows?: boolean;
};

/**
 * Puts a stable, column-scoped `data-testid` on the `.ag-cell` element of
 *  every cell in the given columns, for product analytics (Pendo) and e2e.
 *
 * See `GRID_CELL_TEST_IDS_README.md` in this folder for the full pattern,
 *  the naming convention and the known limits.
 *
 * WHY THIS SHAPE. AG Grid 36 has no ColDef option for an arbitrary attribute
 *  on a cell — a cell exposes only `cellClass`, `cellClassRules`, `cellStyle`
 *  and `cellAriaRole`, and the grid owns the cell DOM. This is the same
 *  constraint that forces the focused-cell accessible name to be applied
 *  imperatively (see `gridFocus/focusedCellAriaLabel.ts`).
 *
 *  `cellRendererSelector` is the one per-cell hook that runs on every cell
 *  render with the cell element in hand, AND is a no-op when it returns
 *  `undefined`: AG Grid then falls back to the column's own `cellRenderer`
 *  if it has one, and to its plain-text default if it does not. Nothing is
 *  wrapped, so `valueFormatted`, `wrapText`/`autoHeight` measurement,
 *  `enableCellChangeFlash`, `cellStyle`, tooltips and the cell's
 *  `textContent` — which the focused-cell aria-label reads — are untouched.
 *
 * Tag the cell, not the content: in read mode the cell IS the click target,
 *  and the interactions worth measuring (dead clicks on a cell that is not
 *  editable yet) produce no inner element at all.
 *
 * A column that needs a `cellRendererSelector` of its own cannot inherit this
 *  one — AG Grid merges `defaultColDef` and `ColDef` per property, so the
 *  column's value replaces it. Compose the two with
 *  `withCellTestIdRendererSelector` instead.
 *
 * @example
 * const defaultColDef: ColDef = {
 *   cellRendererSelector: createCellTestIdRendererSelector({
 *     testIdByColId: journalLineItemCellTestIds,
 *   }),
 * };
 */
export const createCellTestIdRendererSelector = <TData = any, TValue = any>(
  args: CreateCellTestIdRendererSelectorArgs
): CellRendererSelectorFunc<TData, TValue> => {
  return (params: ICellRendererParams<TData, TValue>) => {
    const colId = params.column?.getColId();
    const testId = colId ? args.testIdByColId[colId] : undefined;
    const isPinnedRow = !!params.node?.rowPinned;

    if (
      testId &&
      (args.includePinnedRows === true || !isPinnedRow) &&
      // A refresh re-runs this for the same cell (the invoice grid refreshes
      //  every cell per keystroke), and setAttribute queues a DOM mutation
      //  record even when the value is unchanged.
      params.eGridCell?.getAttribute(CELL_TEST_ID_ATTRIBUTE) !== testId
    ) {
      params.eGridCell?.setAttribute(CELL_TEST_ID_ATTRIBUTE, testId);
    }

    /**
     * MUST stay `undefined`. Returning a component here would replace the
     *  content of every column that relies on AG Grid's default text
     *  renderer.
     */
    return undefined;
  };
};

/**
 * Makes a column that has its own `cellRendererSelector` keep its cell test id.
 *
 * AG Grid builds a column's effective definition by merging `defaultColDef`
 *  and the `ColDef` **per property**, so a column that sets
 *  `cellRendererSelector` replaces the grid-wide tagging one rather than
 *  running both, and every cell in that column stays untagged. That is silent:
 *  the column renders correctly, only the analytics contract has a hole.
 *
 * The tagging selector runs first and for its side effect only — it always
 *  returns `undefined` — so the column selector decides the component
 *  unchanged, including when it returns `undefined` to fall back to the
 *  column's `cellRenderer` or to AG Grid's plain-text default.
 *
 * @example
 * // in the column definition
 * cellRendererSelector: withCellTestIdRendererSelector(
 *   invoiceLineItemCellTestIdRendererSelector,
 *   (params) => {
 *     return isInventoryItem(params.data)
 *       ? { component: InventoryItemNameCellRenderer }
 *       : undefined;
 *   }
 * ),
 */
export const withCellTestIdRendererSelector = <TData = any, TValue = any>(
  cellTestIdRendererSelector: CellRendererSelectorFunc<TData, TValue>,
  columnRendererSelector: CellRendererSelectorFunc<TData, TValue>
): CellRendererSelectorFunc<TData, TValue> => {
  return (params: ICellRendererParams<TData, TValue>) => {
    cellTestIdRendererSelector(params);

    return columnRendererSelector(params);
  };
};
